import { StatPill } from '@components';
import { RmmStatus } from '@interfaces';
import { Popover } from 'antd';
import { AlertTriangle, LucideIcon, Monitor, Server, Wifi } from 'lucide-react';
import React, { useState } from 'react';
import { X } from 'react-feather';

type SummaryStats = {
  totalHosts: number;
  totalVms: number;
  totalNodes: number;
  online: number;
  warning: number;
  offline: number;
  onlinePercent: number;
};

type IssueItem = {
  entityId: string;
  name: string;
  type: 'Host' | 'VM';
  status: 'offline' | 'warning';
  reason: string;
  lastContact?: string;
};

export interface ServerParkSummaryProps {
  stats: SummaryStats;
  issues: IssueItem[];

  query: string;
  onQueryChange: (v: string) => void;

  showOnlyRunning: boolean;
  onShowOnlyRunningChange: (v: boolean) => void;

  onReset: () => void;
  onIssueClick?: (entityId: string) => void;
}

function IconBox({
  icon: Icon,
  colorClass,
  size = 48,
}: {
  icon: LucideIcon;
  colorClass: string;
  size?: number;
}) {
  return (
    <div
      className={`flex items-center justify-center rounded-2xl flex-shrink-0 ${colorClass}`}
      style={{ width: size, height: size }}
    >
      <Icon size={size * 0.5} strokeWidth={2.5} />
    </div>
  );
}

function StatusBar({
  online,
  warning,
  offline,
}: {
  online: number;
  warning: number;
  offline: number;
}) {
  const total = online + warning + offline;
  if (total === 0)
    return (
      <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full" />
    );

  const pOnline = (online / total) * 100;
  const pWarning = (warning / total) * 100;
  const pOffline = (offline / total) * 100;

  return (
    <div className="w-full h-2.5 flex rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 shadow-inner">
      <div
        style={{ width: `${pOnline}%` }}
        className="bg-emerald-400 h-full transition-all duration-500 ease-out"
      />
      <div
        style={{ width: `${pWarning}%` }}
        className="bg-amber-300 h-full transition-all duration-500 ease-out"
      />
      <div
        style={{ width: `${pOffline}%` }}
        className="bg-red-400 h-full transition-all duration-500 ease-out"
      />
    </div>
  );
}

function formatLastContact(iso?: string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

function deriveReason(status: RmmStatus): string {
  if (status === 'offline') return 'Offline';
  return 'Warning';
}

export function ServerParkSummary({
  stats,
  issues,
  onIssueClick,
  query,
  onQueryChange,
  showOnlyRunning,
  onShowOnlyRunningChange,
  onReset,
}: ServerParkSummaryProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  return (
    <div className="flex flex-col xl:flex-row items-stretch xl:items-center gap-4 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 mb-6 shadow-sm">
      {/* STATS SECTION */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[auto_auto_1fr_auto] gap-3 w-full xl:flex-[3]">
        <StatPill className="w-full sm:w-auto bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
          <IconBox icon={Server} colorClass="bg-sky-500/10 text-sky-500" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Hosts
            </div>
            <div className="text-3xl font-bold tracking-tight">
              {stats.totalHosts}
            </div>
          </div>
        </StatPill>
        {/* VMs */}
        <StatPill className="w-full sm:w-auto bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
          <IconBox
            icon={Monitor}
            colorClass="bg-violet-500/10 text-violet-500"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
              VMs
            </div>
            <div className="text-3xl font-bold tracking-tight">
              {stats.totalVms}
            </div>
          </div>
        </StatPill>
        {/* Status */}
        <div className="flex-[1.5] min-w-0 flex flex-col justify-center gap-3 px-6 h-[90px] rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-all">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Wifi size={18} className="text-emerald-500" />
              <span className="text-sm font-semibold text-slate-400">
                Status
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-emerald-500">
                {stats.online}
              </span>
              <span className="text-sm font-bold text-slate-400">
                / {stats.totalNodes}
              </span>
            </div>
          </div>

          <StatusBar
            online={stats.online}
            warning={stats.warning}
            offline={stats.offline}
          />

          <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
            <span>{stats.onlinePercent}% Healthy</span>
            <div className="flex gap-3">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />{' '}
                {stats.warning} Warn
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />{' '}
                {stats.offline} Down
              </span>
            </div>
          </div>
        </div>
        {/* Issues */}
        <Popover
          content={
            <IssuesList
              issues={issues}
              offlineCount={stats.offline}
              warningCount={stats.warning}
              onRowClick={(entityId: string) => {
                setPopoverOpen(false);
                onIssueClick?.(entityId);
              }}
            />
          }
          trigger="click"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          placement="bottom"
          arrow={false}
        >
          <div className="flex-1">
            <StatPill
              className={`w-full sm:w-auto cursor-pointer ${
                issues.length > 0
                  ? 'bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800'
              }`}
            >
              <IconBox
                icon={AlertTriangle}
                colorClass={
                  issues.length > 0
                    ? 'bg-red-400 text-white'
                    : 'bg-slate-200 dark:bg-slate-800 text-slate-400'
                }
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Issues
                </div>
                <div
                  className={`text-3xl font-bold ${issues.length > 0 ? 'text-red-500' : 'text-slate-400'}`}
                >
                  {issues.length}
                </div>
              </div>
            </StatPill>
          </div>
        </Popover>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 w-full xl:flex-[2]">
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search topology..."
          className="h-11 flex-1 min-w-0 bg-transparent text-sm outline-none font-medium"
        />
        <div className="flex items-center gap-3 sm:ml-auto w-full sm:w-auto">
          <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700" />

          <button
            onClick={() => onShowOnlyRunningChange(!showOnlyRunning)}
            className={`h-11 px-4 rounded-xl text-sm font-semibold transition-all ${
              showOnlyRunning
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-600 dark:text-slate-300 bg-white/85 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700'
            }`}
          >
            Running Only
          </button>

          <button
            onClick={onReset}
            className="h-11 px-4 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} className="text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

function IssuesList({
  issues,
  offlineCount,
  warningCount,
  onRowClick,
}: {
  issues: IssueItem[];
  offlineCount: number;
  warningCount: number;
  onRowClick: (entityId: string) => void;
}) {
  return (
    <div className="w-[500px]">
      <div className="flex gap-2 p-3 border-b border-slate-100 dark:border-slate-800">
        {offlineCount > 0 && (
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-bold uppercase">
            {offlineCount} Offline
          </span>
        )}
        {warningCount > 0 && (
          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md text-xs font-bold uppercase">
            {warningCount} Warning
          </span>
        )}
      </div>

      <div className="max-h-[400px] overflow-auto">
        {issues.map((issue, i) => (
          <div
            key={i}
            onClick={() => onRowClick(issue.entityId)}
            className="flex items-center gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer border-b last:border-0 border-slate-50 dark:border-slate-800"
          >
            <div
              className={`w-3 h-3 rounded-full ${issue.status === 'offline' ? 'bg-red-400' : 'bg-amber-300'}`}
            />
            <div className="flex-1">
              <div className="font-bold text-sm">{issue.name}</div>
              <div className="text-xs text-slate-400">
                {issue.type} • {issue.reason ?? deriveReason(issue.status)} •{' '}
                {formatLastContact(issue.lastContact)}
              </div>
            </div>
            <div className="text-xs font-mono font-bold text-slate-500">
              {issue.status.toUpperCase()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ServerParkSummary;
